/**
 * QuarterGrid: the quarter as THREE month calendars side by side, each with its month
 * name, its weekday row and its dated days (ADR-026's hand-rolled quarter view).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Wave W2: Mahmoud's walk found the old quarter view incomprehensible: three bare
 * columns of 31 rows with unlabeled dots. A quarter is now read the way a wall planner
 * reads it: three months, each a real calendar, the marks explained by the legend above,
 * and a day with records opens like it does on the month view. On a phone the three
 * months stack.
 */

import { strings } from "../brand/strings";

import { MonthGrid } from "./MonthGrid";
import type { CalendarCell } from "./model";

export interface QuarterMonth {
  readonly year: number;
  /** Month index 0-11. */
  readonly month: number;
  readonly cells: readonly (CalendarCell & { readonly year: number; readonly month: number })[];
}

export function QuarterGrid({
  months,
  onSelectDay,
}: {
  readonly months: readonly QuarterMonth[];
  /** Receives the ISO date of a clicked cell that carries records. */
  readonly onSelectDay: (iso: string) => void;
}): React.JSX.Element {
  return (
    <div className="q-cal__quarter" data-testid="quarter-grid">
      {months.map((one) => {
        const name = strings.calendar.monthNames[one.month] ?? "";
        const count = one.cells
          .filter((cell) => cell.inMonth)
          .reduce((sum, cell) => sum + cell.dots.length, 0);
        return (
          <section
            key={`${String(one.year)}-${String(one.month)}`}
            className="q-cal__quarter-month"
            aria-label={strings.calendar.monthOf(name, one.year)}
            data-testid="quarter-month"
          >
            <h4 className="q-cal__quarter-heading">
              {name}
              <span className="q-cal__quarter-count">{strings.calendar.dayCount(count)}</span>
            </h4>
            <MonthGrid cells={one.cells} onSelectDay={onSelectDay} compact />
          </section>
        );
      })}
    </div>
  );
}
